import apiClient from '@/services/api/apiClient';
import type { MQL } from '@/services/api/mqlService';

// ── Stage metadata (mirrors ABMJourneyPage) ──────────────────────────────────
const STAGES: { id: number; title: string; phase: string }[] = [
  { id: 1,  title: 'Account & Contact Context', phase: 'Discovery' },
  { id: 2,  title: 'Maturity Mirror Exposure',  phase: 'Discovery' },
  { id: 3,  title: 'Role-Based Pain Framing',   phase: 'Discovery' },
  { id: 4,  title: 'Maturity Gap Awareness',    phase: 'Discovery' },
  { id: 5,  title: 'MES Reality Alignment',     phase: 'Discovery' },
  { id: 6,  title: 'Alignment Call #1',         phase: 'Advisory' },
  { id: 7,  title: 'Architecture Thinking',     phase: 'Advisory' },
  { id: 8,  title: 'Multi-System Coexistence',  phase: 'Advisory' },
  { id: 9,  title: 'Data Readiness',            phase: 'Education' },
  { id: 10, title: 'Automation Timing',         phase: 'Education' },
  { id: 11, title: 'Decision Intelligence',     phase: 'Education' },
  { id: 12, title: 'Confirmation Call #2',      phase: 'Validation' },
  { id: 13, title: 'Future-State Snapshot',     phase: 'Validation' },
  { id: 14, title: 'MQL Validation & Handoff',  phase: 'Validation' },
];

const MATURITY_LABELS: Record<string, string> = {
  L1: 'L1 · Manual / paper',
  L2: 'L2 · Basic digitalisation',
  L3: 'L3 · Connected systems',
  L4: 'L4 · Analytics-driven',
  L5: 'L5 · AI-optimised',
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const esc = (v: any): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmtDate = (d?: string | null): string => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d?: string | null): string => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// A stage form field, label-mapped for the report
const STAGE_FIELDS: { key: string; label: string }[] = [
  { key: 'trigger',    label: 'Trigger' },
  { key: 'plantType',  label: 'Plant Type' },
  { key: 'activities', label: 'Key Activities' },
  { key: 'response',   label: 'Contact Response' },
  { key: 'nextSteps',  label: 'Next Steps' },
  { key: 'notes',      label: 'Notes' },
  { key: 'engagement', label: 'Engagement' },
];

function stageHasContent(sd: Record<string, any>): boolean {
  return STAGE_FIELDS.some(f => {
    const v = sd?.[f.key];
    return typeof v === 'string' ? v.trim().length > 0 : !!v;
  });
}

// ── Section builders ─────────────────────────────────────────────────────────
function buildHead(mql: MQL): string {
  return `
  <header class="rpt-head">
    <div class="brand">
      <div class="brand-mark">CANTIER</div>
      <div class="brand-sub">ABM Portal · MQL Handoff Report</div>
    </div>
    <div class="head-meta">
      <div><span>Account</span><strong>${esc(mql.accountName)}</strong></div>
      <div><span>Contact</span><strong>${esc(mql.contactName || '—')}</strong></div>
      <div><span>Generated</span><strong>${esc(fmtDateTime(new Date().toISOString()))}</strong></div>
    </div>
  </header>`;
}

function buildSummary(mql: MQL): string {
  const rows: [string, string][] = [
    ['Status', mql.status],
    ['Lead Score', `${mql.score} / 100`],
    ['Submitted By', mql.submittedBy || '—'],
    ['Submitted Date', fmtDate(mql.submittedDate)],
    ['Reviewed By', mql.reviewedBy || '—'],
    ['Approved At', mql.salesAcceptedAt ? fmtDate(mql.salesAcceptedAt) : '—'],
  ];
  const cells = rows
    .map(([k, v]) => `<div class="kv"><span>${esc(k)}</span><strong>${esc(v)}</strong></div>`)
    .join('');
  const salesNotes = mql.salesNotes
    ? `<div class="note-block"><span>Sales Notes</span><p>${esc(mql.salesNotes)}</p></div>` : '';
  return `
  <section class="card">
    <h2>Lead Summary</h2>
    <div class="kv-grid">${cells}</div>
    ${salesNotes}
  </section>`;
}

function buildBant(mql: MQL): string {
  const items: { key: keyof MQL['criteria']; label: string; yes: string; no: string }[] = [
    { key: 'budget',    label: 'Budget',    yes: 'Has allocated budget',           no: 'Budget not confirmed' },
    { key: 'authority', label: 'Authority', yes: 'Has decision-making authority',   no: 'Decision authority not confirmed' },
    { key: 'need',      label: 'Need',      yes: 'Clear business need identified',  no: 'Business need not confirmed' },
    { key: 'timeline',  label: 'Timeline',  yes: 'Has defined timeline',           no: 'Timeline not confirmed' },
  ];
  const met = items.filter(i => mql.criteria[i.key]).length;
  const rows = items.map(i => {
    const ok = mql.criteria[i.key];
    return `<div class="bant-row ${ok ? 'ok' : 'no'}">
      <span class="mark">${ok ? '✓' : '✕'}</span>
      <span class="bant-label">${esc(i.label)}</span>
      <span class="bant-detail">${esc(ok ? i.yes : i.no)}</span>
    </div>`;
  }).join('');
  return `
  <section class="card">
    <h2>BANT Qualification <span class="count">${met}/4 met</span></h2>
    <div class="bant">${rows}</div>
  </section>`;
}

function buildAccount(account: any): string {
  if (!account) return '';
  const tags: string[] = Array.isArray(account.architectureTags) ? account.architectureTags : [];
  const rows: [string, string][] = [
    ['Industry', account.industryVertical || '—'],
    ['Region', account.region || '—'],
    ['Domain', account.domain || '—'],
    ['Primary Pain Axis', account.primaryPainAxis || '—'],
    ['Maturity Band', account.maturityBand ? (MATURITY_LABELS[account.maturityBand] || account.maturityBand) : '—'],
    ['ABM Tier', account.abmTier != null ? `Tier ${account.abmTier}` : '—'],
    ['Data Readiness', account.dataReadinessLevel || '—'],
    ['Automation Stage', account.automationStage || '—'],
  ];
  const cells = rows
    .map(([k, v]) => `<div class="kv"><span>${esc(k)}</span><strong>${esc(v)}</strong></div>`)
    .join('');
  const win = account.winTheme
    ? `<div class="note-block"><span>Win Theme</span><p class="italic">${esc(account.winTheme)}</p></div>` : '';
  const tagRow = tags.length
    ? `<div class="tags">${tags.map(t => `<span class="tag">${esc(t)}</span>`).join('')}</div>` : '';
  return `
  <section class="card">
    <h2>Account Intelligence</h2>
    <div class="kv-grid">${cells}</div>
    ${win}
    ${tagRow}
  </section>`;
}

function buildJourney(journey: any): string {
  if (!journey) {
    return `<section class="card"><h2>ABM Journey</h2><p class="muted">No ABM journey on record for this contact.</p></section>`;
  }
  const sd = journey.stageData || {};
  const completed: number[] = Array.isArray(journey.completedStages) ? journey.completedStages : [];
  const pct = Math.round((completed.length / 14) * 100);

  const blocks = STAGES.map(stage => {
    const data = sd[stage.id] || sd[String(stage.id)] || {};
    const done = completed.includes(stage.id);
    const isCurrent = journey.currentStage === stage.id;
    const calls: any[] = Array.isArray(data.callUpdates) ? data.callUpdates : [];
    if (!done && !stageHasContent(data) && calls.length === 0) return ''; // skip empty, untouched stages

    const fields = STAGE_FIELDS
      .filter(f => {
        const v = data[f.key];
        return typeof v === 'string' ? v.trim().length > 0 : !!v;
      })
      .map(f => `<div class="field"><span>${esc(f.label)}</span><p>${esc(data[f.key])}</p></div>`)
      .join('');

    const callTypeLabel = (t: any) =>
      t === 'CALL1' ? 'Alignment Call #1' : t === 'CALL2' ? 'Confirmation Call #2' : t === 'CALL3' ? 'Follow-up Call' : 'Call';
    const callHist = calls.length
      ? `<div class="call-hist">
           <span class="ch-label">Call history (${calls.length})</span>
           ${calls.map((c, i) => {
             const f = c.fields || {};
             const lines = ['activities','response','nextSteps','notes']
               .filter(k => f[k] && String(f[k]).trim())
               .map(k => `<p class="ch-line"><b>${esc(STAGE_FIELDS.find(sf => sf.key === k)?.label || k)}:</b> ${esc(f[k])}</p>`)
               .join('');
             return `<div class="ch-entry">
               <div class="ch-head"><span class="ch-n">${i + 1}</span>${esc(callTypeLabel(c.callType))}<span class="ch-at">${esc(fmtDateTime(c.at))}</span></div>
               ${lines || '<p class="ch-line muted">No fields captured on this call.</p>'}
             </div>`;
           }).join('')}
         </div>`
      : '';

    const statusTag = done
      ? `<span class="st-tag done">Completed${data.completedAt ? ' · ' + esc(fmtDate(data.completedAt)) : ''}</span>`
      : isCurrent ? `<span class="st-tag current">In progress</span>` : '';

    return `
    <div class="stage ${done ? 'is-done' : ''}">
      <div class="stage-head">
        <span class="stage-num">${stage.id}</span>
        <div class="stage-titles">
          <strong>${esc(stage.title)}</strong>
          <span class="phase">${esc(stage.phase)}</span>
        </div>
        ${statusTag}
      </div>
      ${fields ? `<div class="fields">${fields}</div>` : (calls.length ? '' : '<p class="muted small">No details captured.</p>')}
      ${callHist}
    </div>`;
  }).filter(Boolean).join('');

  return `
  <section class="card">
    <h2>ABM Journey <span class="count">Stage ${esc(journey.currentStage || 1)} / 14 · ${completed.length} completed</span></h2>
    <div class="progress"><div class="progress-bar" style="width:${pct}%"></div></div>
    <div class="progress-label">${pct}% complete</div>
    <div class="stages">${blocks || '<p class="muted">No stage activity recorded yet.</p>'}</div>
  </section>`;
}

function buildTimeline(timeline: any): string {
  const events: any[] = Array.isArray(timeline?.data) ? timeline.data : [];
  const summary = timeline?.summary || {};
  const summaryRow = (summary.totalEvents != null)
    ? `<div class="tl-summary">
        <span><strong>${esc(summary.totalCalls ?? 0)}</strong> calls</span>
        <span><strong>${esc(summary.totalDeliveries ?? 0)}</strong> magnet deliveries</span>
        <span><strong>${esc(summary.totalEngagements ?? 0)}</strong> engagements</span>
        <span><strong>${esc(summary.totalEvents ?? 0)}</strong> total events</span>
      </div>` : '';

  if (!events.length) {
    return `<section class="card"><h2>Interaction Timeline</h2>${summaryRow}<p class="muted">No interactions recorded.</p></section>`;
  }

  const rows = events.map(ev => `
    <div class="tl-row">
      <div class="tl-when">${esc(fmtDateTime(ev.timestamp))}</div>
      <div class="tl-body">
        <div class="tl-top">
          <span class="tl-type type-${esc((ev.type || '').toLowerCase())}">${esc((ev.type || '').replace('_', ' '))}</span>
          <strong>${esc(ev.title || '')}</strong>
        </div>
        ${ev.description ? `<p class="tl-desc">${esc(ev.description)}</p>` : ''}
        ${ev.contactName ? `<span class="tl-contact">${esc(ev.contactName)}</span>` : ''}
      </div>
    </div>`).join('');

  return `
  <section class="card">
    <h2>Interaction Timeline</h2>
    ${summaryRow}
    <div class="timeline">${rows}</div>
  </section>`;
}

function buildNotes(mql: MQL): string {
  if (!mql.notes || !mql.notes.trim()) return '';
  return `
  <section class="card">
    <h2>MQL Notes</h2>
    <p class="prewrap">${esc(mql.notes)}</p>
  </section>`;
}

function buildCallLog(history: any): string {
  const calls: any[] = Array.isArray(history?.data) ? history.data : [];
  if (!calls.length) return '';
  const label = (t: any) =>
    t === 'CALL1' ? 'Alignment Call #1' : t === 'CALL2' ? 'Confirmation Call #2' : t === 'CALL3' ? 'Follow-up Call' : (t || 'Call');
  const dur = (s: any) => {
    const n = Number(s);
    if (!n || isNaN(n)) return '';
    const m = Math.floor(n / 60), sec = n % 60;
    return `${m}m ${sec}s`;
  };
  // oldest first so the log reads chronologically
  const ordered = [...calls].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const rows = ordered.map((c, i) => `
    <div class="clog-entry">
      <div class="clog-head">
        <span class="clog-n">${i + 1}</span>
        <strong>${esc(label(c.callType))}</strong>
        <span class="clog-prov">${esc((c.provider || '').toUpperCase())}</span>
        ${c.status ? `<span class="clog-status">${esc(c.status)}</span>` : ''}
        <span class="clog-at">${esc(fmtDateTime(c.createdAt))}${c.duration ? ' · ' + esc(dur(c.duration)) : ''}</span>
      </div>
      ${c.summary ? `<div class="clog-field"><span>Summary</span><p>${esc(c.summary)}</p></div>` : ''}
      ${c.transcript ? `<div class="clog-field"><span>Transcript</span><p class="clog-tx">${esc(c.transcript)}</p></div>` : ''}
    </div>`).join('');
  return `
  <section class="card">
    <h2>AI Call Log <span class="count">${calls.length} call${calls.length === 1 ? '' : 's'}</span></h2>
    <div class="clog">${rows}</div>
  </section>`;
}

function buildFeedback(mql: MQL): string {
  const log: any[] = Array.isArray((mql as any).feedbackLog) ? (mql as any).feedbackLog : [];
  if (!log.length) return '';
  const rows = log.map((f, i) => `
    <div class="ch-entry">
      <div class="ch-head"><span class="ch-n">${i + 1}</span>${esc(f.author || 'Unknown')}<span class="ch-at">${esc(fmtDateTime(f.at))}</span></div>
      <p class="ch-line">${esc(f.text)}</p>
    </div>`).join('');
  return `
  <section class="card">
    <h2>Sales / BD Feedback <span class="count">${log.length} note${log.length === 1 ? '' : 's'}</span></h2>
    <div class="call-hist" style="margin-left:0;border-top:none;padding-top:0">${rows}</div>
  </section>`;
}

// ── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #1a1d23; margin: 0; padding: 32px; background: #f4f5f7; font-size: 13px; line-height: 1.5; }
  .sheet { max-width: 860px; margin: 0 auto; background: #fff; padding: 40px 44px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
  .rpt-head { display: flex; justify-content: space-between; align-items: flex-start;
    border-bottom: 3px solid #1f6feb; padding-bottom: 18px; margin-bottom: 24px; gap: 24px; }
  .brand-mark { font-size: 22px; font-weight: 800; letter-spacing: 2px; color: #1f6feb; }
  .brand-sub { font-size: 11px; color: #6b7280; margin-top: 2px; letter-spacing: .3px; }
  .head-meta { display: grid; gap: 4px; text-align: right; font-size: 11px; }
  .head-meta span { color: #6b7280; margin-right: 6px; }
  .head-meta strong { color: #1a1d23; }
  h2 { font-size: 14px; margin: 0 0 14px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;
    display: flex; justify-content: space-between; align-items: baseline; }
  .count, .st-tag { font-size: 10px; font-weight: 600; color: #6b7280; }
  .card { margin-bottom: 22px; page-break-inside: avoid; }
  .kv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 28px; }
  .kv { display: flex; flex-direction: column; }
  .kv span { font-size: 10px; text-transform: uppercase; letter-spacing: .4px; color: #9099a5; }
  .kv strong { font-size: 13px; }
  .note-block { margin-top: 14px; }
  .note-block span { font-size: 10px; text-transform: uppercase; letter-spacing: .4px; color: #9099a5; }
  .note-block p { margin: 4px 0 0; }
  .italic { font-style: italic; color: #374151; }
  .tags { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px; }
  .tag { font-size: 10px; background: #eef2f7; color: #475569; padding: 2px 8px; border-radius: 4px;
    font-family: ui-monospace, Menlo, monospace; }
  .bant { display: grid; gap: 8px; }
  .bant-row { display: grid; grid-template-columns: 22px 96px 1fr; align-items: center; gap: 8px;
    padding: 7px 10px; border-radius: 6px; background: #f8fafc; }
  .bant-row .mark { font-weight: 700; text-align: center; }
  .bant-row.ok .mark { color: #16a34a; }
  .bant-row.no .mark { color: #dc2626; }
  .bant-label { font-weight: 600; }
  .bant-detail { color: #4b5563; }
  .progress { height: 7px; background: #e5e7eb; border-radius: 4px; overflow: hidden; }
  .progress-bar { height: 100%; background: #1f6feb; }
  .progress-label { font-size: 10px; color: #6b7280; margin: 4px 0 16px; }
  .stages { display: grid; gap: 12px; }
  .stage { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; page-break-inside: avoid; }
  .stage.is-done { border-left: 3px solid #1f6feb; }
  .stage-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
  .stage-num { width: 22px; height: 22px; border-radius: 50%; background: #1f6feb; color: #fff;
    font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex: none; }
  .stage-titles { display: flex; flex-direction: column; flex: 1; }
  .stage-titles strong { font-size: 13px; }
  .phase { font-size: 10px; color: #9099a5; }
  .st-tag { padding: 2px 8px; border-radius: 10px; }
  .st-tag.done { background: #e7f3ec; color: #16794a; }
  .st-tag.current { background: #fff4e5; color: #b45309; }
  .fields { display: grid; gap: 8px; padding-left: 32px; }
  .field span { font-size: 10px; text-transform: uppercase; letter-spacing: .4px; color: #9099a5; }
  .field p { margin: 2px 0 0; white-space: pre-wrap; }
  .call-hist { margin: 10px 0 0 32px; border-top: 1px dashed #e5e7eb; padding-top: 8px; }
  .ch-label { font-size: 10px; text-transform: uppercase; letter-spacing: .4px; color: #9099a5; }
  .ch-entry { margin-top: 8px; padding-left: 10px; border-left: 2px solid #cbd5e1; page-break-inside: avoid; }
  .ch-head { display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 600; color: #334155; }
  .ch-n { width: 16px; height: 16px; border-radius: 50%; background: #e2e8f0; color: #475569;
    font-size: 9px; display: flex; align-items: center; justify-content: center; flex: none; }
  .ch-at { font-weight: 400; color: #9099a5; margin-left: auto; }
  .ch-line { margin: 3px 0 0; font-size: 11px; color: #4b5563; white-space: pre-wrap; }
  .ch-line b { color: #334155; }
  .clog { display: grid; gap: 14px; }
  .clog-entry { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; page-break-inside: avoid; }
  .clog-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
  .clog-n { width: 20px; height: 20px; border-radius: 50%; background: #1f6feb; color: #fff;
    font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex: none; }
  .clog-prov { font-size: 9px; font-weight: 700; letter-spacing: .4px; background: #e0ecff; color: #1d4ed8;
    padding: 2px 6px; border-radius: 4px; }
  .clog-status { font-size: 9px; text-transform: uppercase; letter-spacing: .4px; color: #6b7280;
    background: #f1f5f9; padding: 2px 6px; border-radius: 4px; }
  .clog-at { font-size: 10px; color: #9099a5; margin-left: auto; }
  .clog-field { margin-top: 6px; }
  .clog-field span { font-size: 10px; text-transform: uppercase; letter-spacing: .4px; color: #9099a5; }
  .clog-field p { margin: 2px 0 0; white-space: pre-wrap; }
  .clog-tx { font-size: 11px; color: #475569; max-height: none; }
  .timeline { display: grid; gap: 0; }
  .tl-row { display: grid; grid-template-columns: 132px 1fr; gap: 14px; padding: 10px 0;
    border-bottom: 1px solid #f0f1f3; page-break-inside: avoid; }
  .tl-when { font-size: 11px; color: #6b7280; }
  .tl-top { display: flex; align-items: center; gap: 8px; }
  .tl-type { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px;
    padding: 2px 7px; border-radius: 4px; background: #eef2f7; color: #475569; }
  .type-call { background: #e0ecff; color: #1d4ed8; }
  .type-magnet_delivery { background: #f3e8ff; color: #7c3aed; }
  .type-engagement { background: #dcfce7; color: #15803d; }
  .tl-desc { margin: 3px 0 0; color: #4b5563; }
  .tl-contact { font-size: 10px; color: #9099a5; }
  .tl-summary { display: flex; flex-wrap: wrap; gap: 18px; margin-bottom: 14px; font-size: 11px; color: #6b7280; }
  .tl-summary strong { color: #1a1d23; font-size: 13px; }
  .muted { color: #9099a5; }
  .small { font-size: 11px; }
  .prewrap { white-space: pre-wrap; }
  .rpt-foot { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e5e7eb;
    font-size: 10px; color: #9099a5; text-align: center; }
  .print-bar { position: sticky; top: 0; text-align: center; margin: -16px auto 20px; max-width: 860px; }
  .print-bar button { background: #1f6feb; color: #fff; border: 0; padding: 9px 20px; border-radius: 6px;
    font-size: 13px; font-weight: 600; cursor: pointer; }
  @media print {
    body { background: #fff; padding: 0; }
    .sheet { box-shadow: none; max-width: none; padding: 0; }
    .print-bar { display: none; }
  }
`;

// ── Public API ───────────────────────────────────────────────────────────────
/**
 * Fetches the full picture for an MQL (journey + account + interaction timeline)
 * and opens a print-ready report window. The user saves it as PDF from the
 * browser print dialog. Zero external dependencies.
 */
export async function downloadMqlReport(mql: MQL): Promise<void> {
  // Fetch the supporting data fresh so the report is always complete.
  let journey: any = null;
  let account: any = null;
  let timeline: any = null;
  let callHistory: any = null;

  if (mql.contactId) {
    try {
      const r = await apiClient.get(`/api/abm-journeys/contact/${mql.contactId}`);
      if (r.data && r.data.id) journey = r.data;
    } catch { /* no journey — report still renders */ }
    try {
      const r = await apiClient.get(`/api/ai-calling/history/${mql.contactId}`);
      if (r.data) callHistory = r.data;
    } catch { /* non-fatal */ }
  }
  if (mql.accountId) {
    try {
      const r = await apiClient.get(`/api/accounts/${mql.accountId}`);
      if (r.data) account = r.data;
    } catch { /* non-fatal */ }
    try {
      const r = await apiClient.get(`/api/accounts/${mql.accountId}/timeline`, { params: { limit: 200 } });
      if (r.data) timeline = r.data;
    } catch { /* non-fatal */ }
  }

  const body = [
    buildHead(mql),
    buildSummary(mql),
    buildBant(mql),
    buildAccount(account),
    buildJourney(journey),
    buildCallLog(callHistory),
    buildTimeline(timeline),
    buildFeedback(mql),
    buildNotes(mql),
    `<footer class="rpt-foot">Cantier ABM Portal · Confidential · Generated ${esc(fmtDateTime(new Date().toISOString()))}</footer>`,
  ].join('\n');

  const title = `MQL Report — ${mql.accountName}${mql.contactName ? ' — ' + mql.contactName : ''}`;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${esc(title)}</title>
  <style>${STYLES}</style>
</head>
<body>
  <div class="print-bar"><button onclick="window.print()">Save as PDF / Print</button></div>
  <div class="sheet">${body}</div>
  <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 350); });</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    return;
  }

  // Popup blocked — fall back to downloading a standalone HTML file.
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^\w.-]+/g, '_')}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
